/**
 * Store Pinia des animaux (composition API)
*/
import { defineStore } from "pinia";
import { ref, computed, DeepReadonly } from "vue";
import AnimalService from "../services/AnimalService";
import { IAnimal } from "../models/animals";
import { IBoar } from "../models/boar";
import { IFilterAnimal } from "../models/filterAnimal";
import { StateOfReform } from "../models/reform";
import { useUsersStore } from "./users.store";
import { IAnimalPDTO } from "../models/dtos/animalPDTO";
import { IBoarPDTO } from "../models/dtos/boarPDTO";
import { useParameterStore } from "./parameter.store";
import { useCenterStore } from "./center.store";
import { useThirdPartiesStore } from "./thirdParties.store";
import { useCTG } from "./ctg.store";
import { useBreedingStud } from "./breedingStuds.store";
import { usePSLotStore } from './sanitaire/psLot.store';
import { IAnimalApiResponse } from "../models/api/animalApiResponse";
import signalRManager from "../helper/signalRManager";
import IPayloadStore from "../models/api/payloadStore";

/**
 * Store Pinia des animaux (verrats / lapins) utilisant la Composition API
 */
const useAnimals = defineStore("animals", () => {
    // State
    const datas = ref<Array<DeepReadonly<IAnimal>>>([]);
    const dataFiltres = ref<Array<IAnimal>>([]);
    const current = ref<DeepReadonly<IAnimal>>({} as IBoar | IAnimal);
    const currentAnimalProd = ref<IAnimal>({} as IBoar | IAnimal);
    const modify = ref<IAnimalPDTO>({} as IAnimalPDTO);
    const isLoading = ref<boolean>(false);

    // Filtre persistant pour la liste des animaux
    const filterAnimaux = ref<IFilterAnimal>({
        page: 1,
        pageSize: 5000,
        state: undefined,
        search: "",
        isUsual: undefined,
        isReform: undefined,
        idCenter: undefined,
        typeAnimal: "PO"
    } as IFilterAnimal);

    // Getters
    /**
     * Récupère la liste des animaux non réformés et qui sont présents dans le centre en cours de l'utilisateur (+ eventuellement les centres de quarantaine)
     */
    const getAnimauxInProductionCenter = computed(() => (centerId: number) => {
        return datas.value.filter((animal) =>
            (animal.stud?.id == centerId || animal.stud?.parent?.id == centerId) &&
            animal.reform?.state != StateOfReform.Validate
        );
    });

    const searchByTatoo = computed(() => (centerId: number, search: string) => {
        return datas.value.filter((animal) =>
            (animal.stud?.id == centerId || animal.stud?.parent?.id == centerId) &&
            animal.reform?.state != StateOfReform.Validate &&
            (animal.tattooIdentification?.toLowerCase().includes(search.toLowerCase()))
        );
    });

    // Actions
    async function load() {
        datas.value = [];
        isLoading.value = true;

        await loadAnimaux(
            filterAnimaux.value.page,
            filterAnimaux.value.pageSize,
            filterAnimaux.value.idCenter,
            filterAnimaux.value.typeAnimal,
            filterAnimaux.value.search,
            filterAnimaux.value.isUsual,
            filterAnimaux.value.isReform,
        );

        isLoading.value = false;
    }

    /**
     * Recherche et Stockage des animaux correspondants au filtre
     *
     * @param page Numéro de la page souhaitée
     * @param pageSize Nombre de résultats par page
     * @param idCenter ID du centre recherché
     * @param typeAnimal Espece de l'animal ('PO' / 'CU')
     * @param search Recherche en fonction du numéro de travail / tatouage ou nom
     * @returns Liste des animaux
     */
    async function loadAnimaux(
        page: number,
        pageSize: number,
        idCenter: number | undefined,
        typeAnimal: string | undefined,
        search: string | undefined,
        isUsual: boolean | undefined,
        isReform: boolean | undefined,
    ) {
        filterAnimaux.value.page = page;
        filterAnimaux.value.pageSize = pageSize;
        filterAnimaux.value.idCenter = idCenter;
        filterAnimaux.value.typeAnimal = typeAnimal;
        filterAnimaux.value.search = search;
        filterAnimaux.value.isUsual = isUsual;
        filterAnimaux.value.isReform = isReform;
        let result;
        if (search == "undefined")
            search = "";
        if (typeAnimal == "PO") {
            result = await AnimalService.getBoars(
                filterAnimaux.value,
            );
        } else {
            result = await AnimalService.getAnimals(
                filterAnimaux.value,
            );
        }

        if (!result.error) {
            // Hydrater en batch (optimisé avec Map lookups O(1))
            datas.value = await hydrateAnimals(result.data as IAnimalApiResponse[]);
        } else {
            datas.value = [];
        }
        return result;
    }

    /**
     * Recherche et stockage de l'animal en cours de consultation
     *
     * @param id ID de l'animal à récupérer
     * @returns Animal souhaité
     */
    async function setCurrentAnimal(id: number) {
        let userStore = useUsersStore();
        let dataAnimal;
        if (userStore.currentCenter.species?.code3 == 'PO') {
            dataAnimal = await AnimalService.getBoarById(id);
        } else {
            dataAnimal = await AnimalService.getAnimalById(id);
        }

        if (dataAnimal) {
            // Hydrater l'animal avec les références des stores (batch d'un seul élément)
            const [hydrated] = await hydrateAnimals([dataAnimal as IAnimalApiResponse]);
            current.value = hydrated;
            if (current.value.species.code3 == "PO") {
                modify.value = boarToDTO(current.value as IBoar);
            } else {
                modify.value = animalToDTO(current.value as IAnimal);
            }
        }
        return current.value;
    }

    async function setAnimalProd(animal: IBoar | IAnimal) {
        currentAnimalProd.value = animal;
    }

    /**
     * Création d'un nouvel animal contenu dans modify
     *
     * @returns Information de l'animal nouvellement créé
     */
    async function createAnimal(bVerrat: boolean = false): Promise<{ err: boolean, data: IBoar | IAnimal | unknown }> {
        if (modify.value != undefined) {
            let result = {} as { err: boolean, data: IBoar | IAnimal | unknown };
            if (bVerrat) {
                result = await AnimalService.createBoar(modify.value as IBoarPDTO);
            } else {
                result = await AnimalService.createAnimal(modify.value as IAnimalPDTO);
            }

            // Hydrater l'animal créé avec les références des stores
            if (!result.err && result.data) {
                const [hydrated] = await hydrateAnimals([result.data as IAnimalApiResponse]);
                result.data = hydrated;
            }

            return result;
        } else {
            return { err: true, data: null };
        }
    }

    /**
     * Modification et stockage d'un animal
     *
     * @param data Animal à modifier
     * @returns Animal modifié
     */
    async function updateAnimal() {
        if (modify.value != undefined) {
            let result = {} as { err: boolean, data: IBoar | IAnimal | unknown };
            switch (current.value.species.code3) {
                case 'PO':
                    result = await AnimalService.updateBoar(modify.value as IBoarPDTO);
                    break;
                case 'CU':
                    result = await AnimalService.updateAnimal(modify.value as IAnimalPDTO);
                    break;
            }

            // @ts-ignore // Todo refacto : à corriger
            if (!result.err) {
                // Hydrater l'animal mis à jour avec les références des stores
                const [modelData] = await hydrateAnimals([result.data as IAnimalApiResponse]);

                const index = datas.value.findIndex(({ id }) => id === modify.value.id);

                if (index >= 0) {
                    datas.value.splice(index, 1, modelData);
                } else {
                    datas.value.splice(datas.value.length, 0, modelData);
                }
            }
            return result;
        } else {
            return { err: true, data: null };
        }
    }

    /**
     * Récupération de la dernière quarantaine du centre
     *
     * @param idCenter Quarantaine à vérifier
     * @returns Dernière quarantaine créée
     */
    async function getLastQuarantineCycle(idCenter: number) {
        const cycleQuarantaine = await AnimalService.getLastQuarantineCycle(idCenter);

        if (!cycleQuarantaine.error) {
            return cycleQuarantaine.data;
        }

        return "";
    }

    function animalToDTO(animal: IAnimal): IAnimalPDTO {
        let animalDTO: IAnimalPDTO = {} as IAnimalPDTO;
        Object.assign(animalDTO, animal);
        animalDTO.breedId = animal.breed?.id!;
        animalDTO.ownerId = animal.owner?.id!;
        animalDTO.providerId = animal.provider?.id!;
        animalDTO.productionStateId = animal.productionStatus?.id ?? undefined;
        animalDTO.conservationAptitudeId = animal.conservationAptitude?.id!;
        animalDTO.studId = animal.stud?.id!;
        animalDTO.ctgId = animal.ctg?.id;
        animalDTO.reformId = animal.reform?.id;
        animalDTO.exitWayId = animal.exitWay?.id;
        animalDTO.exitReasonId = animal.exitReason?.id;
        animalDTO.geneticTypeId = animal.geneticType?.id;
        animalDTO.breedingStudId = animal.breedingStud?.id;
        return animalDTO;
    }

    function boarToDTO(boar: IBoar): IBoarPDTO {
        let boarDTO: IBoarPDTO = animalToDTO(boar) as IBoarPDTO;
        boarDTO.armada = boar.armada;
        boarDTO.nnId = boar.nn?.id;
        boarDTO.ifip = boar.ifip;
        boarDTO.countryIFIP = boar.countryIFIP;
        boarDTO.refMax = boar.refMax;
        boarDTO.workNumber = boar.workNumber;
        boarDTO.buckleNumber = boar.buckleNumber;
        return boarDTO;
    }

    /**
     * Hydrate une liste d'animaux en batch (optimisé pour la performance)
     * - Charge tous les stores dépendants une seule fois en parallèle
     * - Crée des Maps d'index pour des lookups O(1)
     * - Évite les appels répétés à useXxxStore() et .find()
     *
     * @param animals Liste d'animaux reçus de l'API avec des IDs
     * @returns Liste d'animaux avec les références des stores
     */
    async function hydrateAnimals(animals: IAnimalApiResponse[]): Promise<DeepReadonly<IAnimal>[]> {
        if (animals.length === 0) return [];

        // 1. Récupérer les stores une seule fois
        const parameterStore = useParameterStore();
        const centerStore = useCenterStore();
        const thirdPartiesStore = useThirdPartiesStore();
        const ctgStore = useCTG();
        const breedingStudsStore = useBreedingStud();
        const psLotStore = usePSLotStore();

        // 2. Charger tous les stores dépendants en parallèle (une seule fois)
        const loadPromises: Promise<unknown>[] = [];

        if (parameterStore.datas.length === 0) {
            loadPromises.push(parameterStore.load({ page: 1, pageSize: 0 }));
        }
        if (centerStore.datas.length === 0) {
            loadPromises.push(centerStore.load({ page: 1, pageSize: 0 }));
        }
        if (!thirdPartiesStore.loadedAll) {
            loadPromises.push(thirdPartiesStore.load({ page: 1, pageSize: 0 }));
        }
        if (ctgStore.listCTG.length === 0) {
            loadPromises.push(ctgStore.loadListCTG());
        }
        if (breedingStudsStore.datas.length === 0) {
            loadPromises.push(breedingStudsStore.loadBreedingStud(1, 0));
        }
        if (psLotStore.datas.length === 0) {
            loadPromises.push(psLotStore.load({ page: 1, pageSize: 0 }));
        }

        if (loadPromises.length > 0) {
            await Promise.all(loadPromises);
        }

        // 3. Créer des Maps d'index pour des lookups O(1) au lieu de O(n)
        const parameterMap = new Map(parameterStore.datas.map(p => [p.id, p]));
        const centerMap = new Map(centerStore.datas.map(c => [c.id, c]));
        const thirdPartiesMap = new Map(thirdPartiesStore.datas.map(t => [t.id, t]));
        const ctgMap = new Map(ctgStore.listCTG.map(c => [c.id, c]));
        const breedingStudMap = new Map(breedingStudsStore.datas.map(b => [b.id, b]));
        const psLotMap = new Map(psLotStore.datas.map(p => [p.id, p]));

        // 4. Hydrater tous les animaux en une seule passe
        const models: DeepReadonly<IAnimal>[] = [];

        for (const animal of animals) {
            const model = { ...animal } as unknown as IAnimal;

            // Paramètres obligatoires (lookup O(1) via Map)
            model.frozenAptitude = parameterMap.get(animal.frozenAptitudeId)!;
            model.conservationAptitude = parameterMap.get(animal.conservationAptitudeId)!;
            model.semenQuality = parameterMap.get(animal.semenQualityId)!;
            model.species = parameterMap.get(animal.speciesId)!;
            model.geneticType = parameterMap.get(animal.geneticTypeId)!;
            model.breed = parameterMap.get(animal.breedId)!;

            // Center (lookup O(1) via Map)
            model.stud = centerMap.get(animal.studId)!;

            // Third parties (lookup O(1) via Map)
            model.owner = thirdPartiesMap.get(animal.ownerId)!;
            model.provider = thirdPartiesMap.get(animal.providerId)!;

            // Paramètres optionnels (lookup O(1) via Map)
            if (animal.psLotId) {
                model.psLot = psLotMap.get(animal.psLotId);
            }
            if (animal.productionStatusId) {
                model.productionStatus = parameterMap.get(animal.productionStatusId);
            }
            if (animal.behaviourId) {
                model.behaviour = parameterMap.get(animal.behaviourId);
            }
            if (animal.exitWayId) {
                model.exitWay = parameterMap.get(animal.exitWayId);
            }
            if (animal.exitReasonId) {
                model.exitReason = parameterMap.get(animal.exitReasonId);
            }
            if (animal.collectDurationId) {
                model.collectDuration = parameterMap.get(animal.collectDurationId);
            }
            if (animal.ctgId) {
                model.ctg = ctgMap.get(animal.ctgId);
            }
            if (animal.breedingStudId) {
                model.breedingStud = breedingStudMap.get(animal.breedingStudId);
            }

            models.push(model);
        }

        return models;
    }

    /**
     * Permet d'initialiser un objet vide pour le formulaire
     */
    async function newForm() {
        modify.value = {
            id: 0,
            barn: 0,
            name: "",
            sex: "",
            nationalTattoo: "",
            tattooIdentification: "",
            birthdate: new Date(),
            exitDate: undefined,
            entryDate: new Date(),
            entryDateLastResidencePlace: new Date(),
            studInfo: "",
            motherNumber: "",
            internalUniqueKey: "",
            motherNation: "",
            fatherNumber: "",
            fatherNation: "",
            quarantineCycle: "",
            corridor: "",
            agrementGDDate: undefined,
            semenAgrementDate: undefined,
            box: 0
        } as IBoarPDTO | IAnimalPDTO;
        return modify.value;
    }

    // Retourner les propriétés et méthodes publiques
    return {
        // State
        datas,
        dataFiltres,
        current,
        currentAnimalProd,
        modify,
        isLoading,
        filterAnimaux,

        // Getters
        getAnimauxInProductionCenter,
        searchByTatoo,

        // Actions
        load,
        loadAnimaux,
        setCurrentAnimal,
        setAnimalProd,
        createAnimal,
        updateAnimal,
        getLastQuarantineCycle,
        animalToDTO,
        boarToDTO,
        hydrateAnimals,
        newForm,
    };
});

signalRManager.connection.on("MajAnimal", async (payload: IPayloadStore) => {
    const store = useAnimals();
    switch (payload.action) {
        case "Create": {
            if (store.datas.findIndex((a) => a.id == payload.data?.id) == -1) {
                const [hydrated] = await store.hydrateAnimals([payload.data as IAnimalApiResponse]);
                store.datas.push(hydrated);
            }
            break;
        }
        case "Update": {
            const idx = store.datas.findIndex((a) => a.id == payload.data?.id);
            if (idx > -1) {
                const [hydrated] = await store.hydrateAnimals([payload.data as IAnimalApiResponse]);
                store.datas.splice(idx, 1, hydrated);
            }
            break;
        }
        case "Delete": {
            const idx = store.datas.findIndex((a) => a.id == payload.id);
            if (idx > -1) store.datas.splice(idx, 1);
            break;
        }
    }
});

export { useAnimals };
